export type AgeAssuranceGateState = 'pending' | 'allowed' | 'blocked';

export type AgeAssuranceBlockedReason = 'under_13' | 'age_sharing_required';

export type AgeAssuranceSnapshot =
  | { state: 'pending'; blockedReason: null }
  | { state: 'allowed'; blockedReason: null }
  | { state: 'blocked'; blockedReason: AgeAssuranceBlockedReason };

export type AgeAssuranceEligibility =
  | 'not_checked'
  | 'eligible'
  | 'not_eligible'
  | 'unknown'
  | 'qa_override';

export type PlatformAgeAssuranceOptions = { forceAgeRequest?: boolean };

export type AgeAssuranceAttemptTrigger = 'startup' | 'retry';

export enum AgeAssuranceMode {
  OFF = 'off',
  LIVE = 'live',
  QA_TEXAS_LIVE = 'qa-texas-live',
  QA_OUTSIDE_TEXAS = 'qa-outside-texas',
  QA_UNDER_13 = 'qa-under-13',
  QA_13_PLUS = 'qa-13-plus',
  QA_UNAVAILABLE = 'qa-unavailable',
}

export type AgeAssuranceNativeResult =
  | 'not_called'
  | 'android_access_not_shared'
  | 'android_verification_required'
  | 'android_access_unavailable'
  | 'response_missing'
  | 'response_under_threshold'
  | 'response_at_or_above_threshold'
  | 'response_ambiguous'
  | 'response_invalid'
  | 'error_not_available'
  | 'error_user_declined'
  | 'error_invalid_request'
  | 'error_no_view_controller'
  | 'android_error_api_not_available'
  | 'android_error_play_store_not_found'
  | 'android_error_network'
  | 'android_error_play_services_not_found'
  | 'android_error_cannot_bind_to_service'
  | 'android_error_play_store_version_outdated'
  | 'android_error_play_services_version_outdated'
  | 'android_error_client_transient'
  | 'android_error_app_not_owned'
  | 'android_error_sdk_version_outdated'
  | 'android_error_internal'
  | 'error_unknown';

export type AgeAssuranceOutcome =
  | 'flag_disabled'
  | 'web_bypass'
  | 'geography_unavailable'
  | 'outside_texas'
  | 'ios_not_eligible'
  | 'age_not_shared'
  | 'android_access_not_shared'
  | 'android_verification_required'
  | 'native_unavailable'
  | 'native_failure'
  | 'age_13_plus'
  | 'age_under_13';

export type UserGeo = {
  country?: unknown;
  state?: unknown;
};

export type AgeRangeBounds = {
  lowerBound?: unknown;
  upperBound?: unknown;
};

export type PlatformAgeAssuranceResult = (
  | ({ outcome: 'age_range' } & AgeRangeBounds)
  | {
      outcome:
        | 'ios_not_eligible'
        | 'age_not_shared'
        | 'android_access_not_shared'
        | 'android_verification_required'
        | 'native_unavailable'
        | 'native_failure';
      nativeResult: AgeAssuranceNativeResult;
    }
) & { eligibility?: AgeAssuranceEligibility };

export type AgeAssuranceTerminalEvent = {
  message: 'Age assurance decision completed';
  ageAssuranceOutcome: AgeAssuranceOutcome;
  ageAssuranceTrigger: AgeAssuranceAttemptTrigger;
  ageAssuranceNativeResult: AgeAssuranceNativeResult;
  ageAssuranceEligibility: AgeAssuranceEligibility;
  ageAssuranceGateState: Exclude<AgeAssuranceGateState, 'pending'>;
  ageAssuranceBlockedReason: AgeAssuranceBlockedReason | null;
};

export type AgeAssuranceTelemetryEvent = AgeAssuranceTerminalEvent & {
  ageAssuranceMode: AgeAssuranceMode;
};

export type AgeAssuranceAttemptDependencies = {
  queryUserGeo: () => Promise<UserGeo | null>;
  requestPlatformAgeAssurance: (
    options?: PlatformAgeAssuranceOptions
  ) => Promise<PlatformAgeAssuranceResult>;
  logTerminalDecision: (event: AgeAssuranceTerminalEvent) => void;
};
